import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import {
   Grid,
   GridItem,
   useColorModeValue,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   useToast,
   useColorMode,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { addStyle, getStyle, updateStyle } from '../../../../../redux/actions/style';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown as renderDynamicUiDropdown } from 'components/ui/DynamicDropdown';
import { getShareCompanyOptions } from '../../../../../utils/util';
import { useGlobal } from 'contexts';
import { useUser } from 'contexts';

// Yup Validation Schema
const validationSchema = Yup.object().shape({
   name: Yup.string().required('Name is required').min(1, 'Name must be at least 1 characters long'),
   notes: Yup.string().required('Notes is required'),
});

const StyleMasterForm = ({
   isOpen,
   onClose,
   isEditMode = false,
   currentRow = null,
   onSave,
   saveandcontinue = true,
}) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [row, setRow] = useState({
      name: '',
      notes: '',
      remarks: '',
      compIds: [],
   });
   const { user } = useUser();
   const [isLoading, setIsLoading] = useState(false);
   const { companyList, userGlobalSettingsData, company } = useGlobal();
   const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.style;
   const shouldShowCompanyDropdown = globalSetting || false;
   const dbName = company?.dbName;

   const handleCompanyChange = useCallback((selectedOptions) => {
      setRow((prev) => ({
         ...prev,
         compIds: selectedOptions ?? [],
      }));
   }, []);

   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );

   const [errors, setErrors] = useState({});
   const toast = useToast();
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const dispatch = useDispatch();

   const clearFormData = () => {
      setRow({
         name: '',
         notes: '',
         remarks: '',
      });
      setErrors({});
   };

   useEffect(() => {
      if (user?.loginhistory?.companyId && !isEditMode) {
         setRow((prev) => {
            if (prev.compIds.length === 0 || !prev.compIds.includes(user.loginhistory.companyId)) {
               return {
                  ...prev,
                  compIds: [user.loginhistory.companyId],
               };
            }
            return prev;
         });
      }
   }, [user?.loginhistory?.companyId, isEditMode]);

   useEffect(() => {
      if (!isEditMode) {
         clearFormData();
         setRow((prev) => ({
            ...prev,
            compIds: user?.loginhistory?.companyId ? [user.loginhistory.companyId] : [],
         }));
      } else if (isEditMode && currentRow) {
         setRow({
            name: currentRow.name || '',
            notes: currentRow.notes || '',
            remarks: currentRow.remarks || '',
            compIds: currentRow.compIds || [],
         });
      }
   }, [isEditMode, currentRow]);

   const validateStep = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
            return false;
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      setRow((prev) => ({
         ...prev,
         [field]: value,
      }));

      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const onFormSubmit = async () => {
      setIsLoading(true);
      const processedData = {
         ...row,
         compIds: row.compIds,
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            processedData.id = currentRow.id;
            await dispatch(updateStyle(processedData));
         } else {
            await dispatch(addStyle(processedData));
         }
         dispatch(getStyle());
         onClose();
         setIsLoading(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving the category.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         setIsLoading(false);
      }
   };

   const onsavecontinue = async () => {
      setIsLoading(true);
      const processedData = {
         ...row,
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            processedData.id = currentRow.id;
            await dispatch(updateStyle(processedData));
         } else {
            await dispatch(addStyle(processedData));
         }
         dispatch(getStyle());
         clearFormData();
         setIsLoading(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving the category.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         setIsLoading(false);
      }
   };

   const handleSubmit = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onFormSubmit();
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handlesavecontinue = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onsavecontinue();
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const nameInputRef = useRef();
   const savefinishRef = useRef();
   const savecontinueRef = useRef();
   const cancelRef = useRef();
   const notesRef = useRef();
   const remarksRef = useRef();
   const companyRef = useRef();

   useEffect(() => {
      setTimeout(() => {
         nameInputRef.current?.focus();
      }, 100);
   }, [isOpen]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' ||
            activeElement?.isContentEditable;

         if (isTyping) return;

         if (isOpen && e.key.toLowerCase() === 's') {
            if (e.shiftKey) {
               handlesavecontinue();
            } else {
               handleSubmit();
            }
         }
         if (isOpen && e.key.toLowerCase() === 'c') {
            onClose();
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [validateStep]);

   useEffect(() => {
      const handleEsc = (e) => {
         if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
         }
      };

      document.addEventListener('keydown', handleEsc);
      return () => {
         document.removeEventListener('keydown', handleEsc);
      };
   }, [onClose]);

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
         <ModalOverlay className={`backdrop-blur-sm ${isDark ? 'bg-black/60' : 'bg-gray-900/20'}`} />
         <ModalContent
            bg={useColorModeValue('white', '#1a202c')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="24px"
            p={0}
            mx="15px"
            mt={{ base: '35%', md: '25%', xl: '15%' }}
            className={`shadow-2xl ${isDark ? 'border border-gray-700/50' : 'border border-gray-200/50'}`}>
            {/* Header with gradient background */}
            <ModalHeader
               className={`
                  text-xl font-bold px-8 py-6 rounded-t-3xl
                  ${isDark
                     ? 'bg-gradient-to-r from-cyan-900/40 via-blue-900/40 to-indigo-900/40 border-b border-gray-700/50'
                     : 'bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 border-b border-gray-200/80'
                  }
               `}>
               <div className="flex items-center gap-3">
                  <div
                     className={`w-2 h-8 rounded-full ${isDark
                        ? 'bg-gradient-to-b from-cyan-500 to-blue-500'
                        : 'bg-gradient-to-b from-cyan-600 to-blue-600'
                        }`}></div>
                  <span className={isDark ? 'text-gray-100' : 'text-gray-800'}>
                     {isEditMode ? 'Edit Style' : 'Add New Style'}
                  </span>
               </div>
            </ModalHeader>

            <ModalCloseButton
               className={`
                  mt-4 mr-4 rounded-full transition-all duration-200
                  ${isDark
                     ? 'hover:bg-gray-700/50 text-gray-400 hover:text-gray-200'
                     : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }
               `}
            />

            <ModalBody className="px-8 py-6">
               <Grid
                  templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }}
                  gap={6}
                  className="mb-2">
                  {/* Name Field */}
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'name',
                        'Name',
                        {
                           value: row.name,
                           onChange: (e) => handleInputChange('name', e.target.value),
                           ref: nameInputRef,
                           onKeyDown: (e) => {
                              if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                 e.preventDefault();
                                 notesRef.current?.focus();
                              }
                           },
                        },
                        errors,
                        textColor,
                        'text',
                        true,
                     )}
                  </GridItem>

                  {/* Notes Field */}
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'notes',
                        'Notes',
                        {
                           value: row.notes,
                           onChange: (e) => handleInputChange('notes', e.target.value),
                           ref: notesRef,
                           onKeyDown: (e) => {
                              if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                 e.preventDefault();
                                 if (shouldShowCompanyDropdown) {
                                    companyRef.current?.focus();
                                 } else {
                                    remarksRef.current?.focus();
                                 }
                              }
                           },
                        },
                        errors,
                        textColor,
                        'text',
                        true,
                     )}
                  </GridItem>

                  {/* Company Dropdown */}
                  {shouldShowCompanyDropdown && (
                     <GridItem className="seperator-width">
                        {renderDynamicUiDropdown(
                           'company',
                           'Company',
                           lastcompanyOptions,
                           {
                              ref: companyRef,
                              value: row.compIds,
                              onChange: handleCompanyChange,
                              onKeyDown: (e) => {
                                 if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                    e.preventDefault();
                                    remarksRef.current?.focus();
                                 }
                              },
                           },
                           {},
                           textColor,
                           false,
                           'Select company',
                           true,
                           undefined,
                           true,
                        )}
                     </GridItem>
                  )}
                  {/* Remarks Field */}
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'remarks',
                        'Remarks',
                        {
                           value: row.remarks,
                           onChange: (e) => handleInputChange('remarks', e.target.value),
                           ref: remarksRef,
                           onKeyDown: (e) => {
                              if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                 e.preventDefault();
                                 savefinishRef.current?.focus();
                              }
                           },
                        },
                        errors,
                        textColor,
                        'text',
                     )}
                  </GridItem>
               </Grid>
            </ModalBody>

            {/* Footer with gradient background */}
            <ModalFooter
               className={`relative rounded-b-3xl ${isDark
                  ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80 border-t border-gray-700'
                  : 'bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200'
                  }`}
               py={6}
               px={8}>
               <div className="flex gap-3">
                  {!isEditMode && saveandcontinue === true && (
                     <Button
                        ref={savecontinueRef}
                        onClick={handlesavecontinue}
                        isLoading={isLoading}
                        disabled={isLoading}
                        className={`group relative overflow-hidden transition-all duration-300 ${isDark
                           ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 border border-emerald-500 shadow-lg shadow-emerald-900/30'
                           : 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 border border-emerald-400 shadow-lg shadow-emerald-300/30'
                           } text-white font-semibold`}
                        borderRadius="xl"
                        px={8}
                        py={3}
                        _focus={{ boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.4)' }}
                        _hover={{ transform: 'translateY(-2px)' }}
                        onKeyDown={(e) => {
                           if (e.key === 'ArrowRight') {
                              e.preventDefault();
                              savefinishRef.current?.focus();
                           }
                        }}>
                        <span className="relative z-10 flex items-center gap-2">
                           <span>💾</span> Save & Continue
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                     </Button>
                  )}

                  <Button
                     onClick={handleSubmit}
                     isLoading={isLoading}
                     disabled={isLoading}
                     ref={savefinishRef}
                     className={`group relative overflow-hidden transition-all duration-300 ${isDark
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 border border-blue-500 shadow-lg shadow-blue-900/30'
                        : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 border border-blue-400 shadow-lg shadow-blue-300/30'
                        } text-white font-semibold`}
                     borderRadius="xl"
                     px={8}
                     py={3}
                     _focus={{ boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.4)' }}
                     _hover={{ transform: 'translateY(-2px)' }}
                     onKeyDown={(e) => {
                        if (e.key === 'ArrowRight') {
                           e.preventDefault();
                           cancelRef.current?.focus();
                        } else if (e.key === 'ArrowLeft') {
                           e.preventDefault();
                           if (!isEditMode && saveandcontinue === true) {
                              savecontinueRef.current?.focus();
                           }
                        }
                     }}>
                     <span className="relative z-10 flex items-center gap-2">
                        <span>✓</span> Save & Finish
                     </span>
                     <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </Button>

                  <Button
                     ref={cancelRef}
                     variant="ghost"
                     onClick={onClose}
                     className={`transition-all duration-300 ${isDark
                        ? 'text-gray-300 hover:text-white hover:bg-red-600/20 border border-gray-600 hover:border-red-500'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-red-50 border border-gray-300 hover:border-red-300'
                        } font-semibold`}
                     borderRadius="xl"
                     px={8}
                     py={3}
                     _focus={{ boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.4)' }}
                     _hover={{ transform: 'translateY(-2px)' }}
                     onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft') {
                           e.preventDefault();
                           savefinishRef.current?.focus();
                        }
                     }}>
                     <span className="flex items-center gap-2">
                        <span>✕</span> Cancel
                     </span>
                  </Button>
               </div>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default StyleMasterForm;
